/**
 * Sign in. The only screen an unauthenticated visitor can reach.
 *
 * `POST /auth/login` is the one anonymous route that is not a status probe. A
 * 401 from it rejects the email and the password *as a pair*: the body is
 * identical whether the address is unknown or the password did not match, and
 * the server does equal work on both paths so the timing does not separate them
 * either. This screen therefore says one thing for both — `SIGN_IN_REJECTED_MESSAGE`,
 * via `uiSafeMessage(..., { during: "sign_in" })` — and never guesses which half
 * was wrong. Splitting the message here would hand back the account-enumeration
 * oracle the API refuses to give (REQ-703).
 *
 * The problem document itself is still rendered underneath, unedited. That is
 * not in tension with the paragraph above: the API returns the same document
 * either way, so showing exactly what it sent discloses nothing it did not, and
 * §5 obliges this UI to surface `request_id` so a person can quote it.
 */
import { useState } from "react";

import type { HalyardClient, LoginResponseBody } from "../api/index.ts";
import { ProblemNotice } from "./ProblemNotice.tsx";

export interface LoginScreenProps {
  readonly client: HalyardClient;
  readonly onSignedIn: (login: LoginResponseBody) => void;
  /** Rendered above the form when a session ended rather than never existed. */
  readonly notice?: string | undefined;
}

export function LoginScreen({ client, onSignedIn, notice }: LoginScreenProps): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const canSubmit = email.trim() !== "" && password !== "" && !submitting;

  const onSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);

    client
      .logIn({ email: email.trim(), password })
      .then((login) => {
        // The token is in this response and nowhere else, ever. Hand it straight
        // to the shell rather than holding it here.
        onSignedIn(login);
      })
      .catch((cause: unknown) => {
        setError(cause);
        setSubmitting(false);
      });
  };

  return (
    <main className="login">
      <form className="login__card" onSubmit={onSubmit}>
        <h1 className="login__title">Halyard Monitor</h1>
        <p className="login__subtitle">Sign in to see your organisation&rsquo;s monitors.</p>

        {notice === undefined ? null : (
          <div className="notice notice--info" role="status">
            <p className="notice__headline">{notice}</p>
          </div>
        )}

        <label className="field">
          <span className="field__label">Email</span>
          <input
            className="field__input"
            type="email"
            name="email"
            autoComplete="username"
            autoFocus
            required
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
        </label>

        <label className="field">
          <span className="field__label">Password</span>
          <input
            className="field__input"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
        </label>

        <button className="button button--primary" type="submit" disabled={!canSubmit}>
          {submitting ? "Signing in…" : "Sign in"}
        </button>

        {error === null ? null : <ProblemNotice error={error} options={{ during: "sign_in" }} />}
      </form>
    </main>
  );
}
